/**
 * Una sola definición de «visitas» y «visitantes únicos» para El Radar,
 * Analítica y el email diario. Siempre por la fecha de cada evento de
 * public_activity (nunca por public_sessions.created_at / last_seen_at):
 * quien vuelve en dos periodos cuenta en los dos.
 */

export type ActivityEvent = { session_key: string | null; created_at: string; visit_id?: string | null };

export const VISIT_GAP_MS = 30 * 60 * 1000;

/** Visitas = visit_id distintos; si falta, se corta la visita tras 30 min sin actividad. Visitantes = rl_vid distintos. */
export function countVisitsAndVisitors(events: ActivityEvent[]): { visits: number; visitors: number } {
  const visitors = new Set<string>();
  const visitIds = new Set<string>();
  const legacy = new Map<string, number[]>();
  for (const e of events) {
    if (!e.session_key) continue;
    visitors.add(e.session_key);
    if (e.visit_id) visitIds.add(`${e.session_key}|${e.visit_id}`);
    else {
      const arr = legacy.get(e.session_key) ?? [];
      arr.push(new Date(e.created_at).getTime());
      legacy.set(e.session_key, arr);
    }
  }
  let legacyVisits = 0;
  for (const times of legacy.values()) {
    times.sort((a, b) => a - b);
    legacyVisits++;
    for (let i = 1; i < times.length; i++) if (times[i]! - times[i - 1]! > VISIT_GAP_MS) legacyVisits++;
  }
  return { visits: visitIds.size + legacyVisits, visitors: visitors.size };
}

/** Inicio de cada visita (misma regla que countVisitsAndVisitors): la serie diaria suma exactamente el KPI. */
export function visitStarts(events: ActivityEvent[]): string[] {
  const starts: string[] = [];
  const firstById = new Map<string, string>();
  const legacy = new Map<string, string[]>();
  for (const e of events) {
    if (!e.session_key) continue;
    if (e.visit_id) {
      const k = `${e.session_key}|${e.visit_id}`;
      const prev = firstById.get(k);
      if (!prev || e.created_at < prev) firstById.set(k, e.created_at);
    } else {
      const arr = legacy.get(e.session_key) ?? [];
      arr.push(e.created_at);
      legacy.set(e.session_key, arr);
    }
  }
  starts.push(...firstById.values());
  for (const arr of legacy.values()) {
    const times = arr.map((t) => new Date(t).getTime()).sort((a, b) => a - b);
    times.forEach((t, i) => { if (i === 0 || t - times[i - 1]! > VISIT_GAP_MS) starts.push(new Date(t).toISOString()); });
  }
  return starts;
}

/** Reparte eventos entre periodo actual (> cut) y anterior, y cuenta cada uno por separado. */
export function splitVisitCounts(events: ActivityEvent[], cutMs: number) {
  const cur: ActivityEvent[] = [];
  const prev: ActivityEvent[] = [];
  for (const e of events) (new Date(e.created_at).getTime() > cutMs ? cur : prev).push(e);
  return { cur: countVisitsAndVisitors(cur), prev: countVisitsAndVisitors(prev) };
}

/**
 * Equipo sin IP: solo por rl_vid registrado (analytics_excluded_devices.visitor_id).
 * Una IP compartida nunca excluye a nadie.
 */
export function isTeamVisitor(sessionKey: string | null | undefined, teamVisitorIds: Set<string>): boolean {
  return !!sessionKey && teamVisitorIds.has(sessionKey);
}

/** «Consultas al Agente»: solo agent_chat de usuarios externos (module_evolution y demás no cuentan). */
export function isAgentConsult(featureType: string | null | undefined, userId: string | null | undefined, teamIds: Set<string>): boolean {
  return featureType === "agent_chat" && !(userId && teamIds.has(userId));
}

const PROD_HOST = /(^|\.)retaillemon\.com$/i;
const BOT_UA = /bot|crawl|spider|slurp|googleother|headless|lighthouse|preview|facebookexternalhit|bingpreview|python|curl|wget/i;

export const isBotUserAgent = (ua: string | null | undefined) => !ua || BOT_UA.test(ua);

/** Host de un error: el guardado o, en filas antiguas, el de la primera URL del stack. */
export function errorHost(row: { host?: string | null; stack?: string | null }): string | null {
  if (row.host) return row.host;
  const m = row.stack?.match(/https?:\/\/([^/\s:]+)/);
  return m?.[1] ?? null;
}

/** Error real de producción: host oficial, no del equipo, no robot. */
export function isProductionError(row: { host?: string | null; stack?: string | null; is_internal?: boolean | null; is_bot?: boolean | null; user_agent?: string | null; user_id?: string | null }, teamIds: Set<string> = new Set()): boolean {
  const host = errorHost(row);
  if (!host || !PROD_HOST.test(host)) return false;
  if (row.is_internal || (row.user_id && teamIds.has(row.user_id))) return false;
  if (row.is_bot || isBotUserAgent(row.user_agent)) return false;
  return true;
}
